import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { vehicleTypeAPI } from '../../services/api';

const API_HOST = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
const resolveImg = (url) => (!url ? null : url.startsWith('http') ? url : `${API_HOST}${url}`);

const VehicleTypesPage = () => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  // Add form
  const [addName, setAddName]   = useState('');
  const [addIcon, setAddIcon]   = useState('');
  const [addMin, setAddMin]     = useState('');
  const [addMax, setAddMax]     = useState('');
  const [addImageFile, setAddImageFile]       = useState(null);
  const [addImagePreview, setAddImagePreview] = useState(null);
  const [adding, setAdding]     = useState(false);
  const addFileRef = useRef();

  // Edit state
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState('');
  const [editMin, setEditMin]   = useState('');
  const [editMax, setEditMax]   = useState('');
  const [editImageFile, setEditImageFile]       = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editRemoveImage, setEditRemoveImage]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const editFileRef = useRef();

  const load = () => {
    setLoading(true);
    vehicleTypeAPI.getAll(true) // include inactive so they can be reactivated
      .then((res) => setRows(res.data.data || []))
      .catch(() => toast.error('فشل تحميل أنواع المركبات'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = rows.filter((r) => r.name.includes(search));

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addName.trim() || !addMin || !addMax) return;
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append('name', addName.trim());
      fd.append('icon', addIcon.trim() || '');
      fd.append('minCapacity', addMin);
      fd.append('maxCapacity', addMax);
      if (addImageFile) fd.append('image', addImageFile);
      await vehicleTypeAPI.create(fd);
      toast.success(`تمت إضافة "${addName.trim()}"`);
      setAddName(''); setAddIcon(''); setAddMin(''); setAddMax('');
      setAddImageFile(null); setAddImagePreview(null);
      if (addFileRef.current) addFileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الإضافة');
    } finally {
      setAdding(false);
    }
  };

  const handleAddImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAddImageFile(file);
    setAddImagePreview(URL.createObjectURL(file));
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditName(row.name);
    setEditIcon(row.icon || '');
    setEditMin(row.minCapacity);
    setEditMax(row.maxCapacity);
    setEditImageFile(null);
    setEditImagePreview(resolveImg(row.imageUrl));
    setEditRemoveImage(false);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditImageFile(file);
    setEditRemoveImage(false);
    setEditImagePreview(URL.createObjectURL(file));
  };

  const handleEditRemoveImage = () => {
    setEditImageFile(null);
    setEditImagePreview(null);
    setEditRemoveImage(true);
    if (editFileRef.current) editFileRef.current.value = '';
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', editName.trim());
      fd.append('icon', editIcon.trim() || '');
      fd.append('minCapacity', editMin);
      fd.append('maxCapacity', editMax);
      if (editImageFile) fd.append('image', editImageFile);
      if (editRemoveImage) fd.append('removeImage', 'true');
      await vehicleTypeAPI.update(id, fd);
      toast.success('تم الحفظ');
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (row) => {
    if (!window.confirm(`إلغاء تفعيل "${row.name}"؟ لن يظهر بعد الآن عند إضافة باص جديد.`)) return;
    try {
      await vehicleTypeAPI.delete(row.id);
      toast.success('تم إلغاء التفعيل');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل إلغاء التفعيل');
    }
  };

  const handleReactivate = async (row) => {
    try {
      const fd = new FormData();
      fd.append('isActive', 'true');
      await vehicleTypeAPI.update(row.id, fd);
      toast.success('تم التفعيل');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل التفعيل');
    }
  };

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">🚌 أنواع المركبات</h1>
        <p className="text-sm text-gray-500 mt-1">القالب الرئيسي لتصنيف الأسطول — تستخدمه الشركات عند إضافة باص، ويظهر في بحث المسافرين. الصورة تظهر في بطاقة اختيار نوع المركبة بواجهة المسافر.</p>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="bg-white rounded-2xl shadow p-5 space-y-4">
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs text-gray-500 mb-1">الاسم</label>
            <input
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="مثال: حافلة كبيرة"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="w-20">
            <label className="block text-xs text-gray-500 mb-1">الأيقونة</label>
            <input
              value={addIcon}
              onChange={(e) => setAddIcon(e.target.value)}
              placeholder="🚌"
              maxLength={4}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="w-24">
            <label className="block text-xs text-gray-500 mb-1">أقل سعة</label>
            <input
              type="number"
              value={addMin}
              onChange={(e) => setAddMin(e.target.value)}
              placeholder="16"
              min={1}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="w-24">
            <label className="block text-xs text-gray-500 mb-1">أقصى سعة</label>
            <input
              type="number"
              value={addMax}
              onChange={(e) => setAddMax(e.target.value)}
              placeholder="30"
              min={1}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={adding || !addName.trim() || !addMin || !addMax}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl px-5 py-2 text-sm font-semibold transition"
          >
            {adding ? '...' : '+ إضافة'}
          </button>
        </div>

        {/* Image upload */}
        <div className="flex items-center gap-3">
          {addImagePreview && (
            <img src={addImagePreview} alt="" className="w-16 h-16 object-cover rounded-xl border border-gray-200" />
          )}
          <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-gray-200 hover:border-blue-300 rounded-xl px-3 py-2 text-xs text-gray-500 transition">
            <input ref={addFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAddImageChange} />
            📷 {addImagePreview ? 'تغيير الصورة' : 'صورة المركبة (اختياري)'}
          </label>
        </div>
      </form>

      {/* Search + table */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
          <span className="font-semibold text-gray-700 text-sm">
            القائمة <span className="text-gray-400 font-normal">({filtered.length})</span>
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث..."
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">جاري التحميل...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">لا توجد نتائج</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-5 py-3 text-right font-medium">الصورة</th>
                  <th className="px-5 py-3 text-right font-medium">الاسم</th>
                  <th className="px-5 py-3 text-right font-medium">السعة</th>
                  <th className="px-5 py-3 text-right font-medium">الحالة</th>
                  <th className="px-5 py-3 text-left font-medium">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((row) => (
                  <tr key={row.id} className={`hover:bg-gray-50/60 ${row.isActive === false ? 'opacity-50' : ''}`}>
                    {editId === row.id ? (
                      <>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            {editImagePreview && (
                              <img src={editImagePreview} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                            )}
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] text-blue-600 hover:text-blue-800 cursor-pointer">
                                <input ref={editFileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleEditImageChange} />
                                تغيير
                              </label>
                              {editImagePreview && (
                                <button type="button" onClick={handleEditRemoveImage} className="text-[10px] text-red-500 hover:text-red-700 text-right">
                                  إزالة
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1.5">
                            <input
                              value={editIcon}
                              onChange={(e) => setEditIcon(e.target.value)}
                              maxLength={4}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-sm w-12 text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={editMin}
                              onChange={(e) => setEditMin(e.target.value)}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-sm w-16 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <span className="text-gray-400">-</span>
                            <input
                              type="number"
                              value={editMax}
                              onChange={(e) => setEditMax(e.target.value)}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-sm w-16 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2 text-gray-400">—</td>
                        <td className="px-4 py-2 text-left">
                          <button
                            onClick={() => handleSave(row.id)}
                            disabled={saving}
                            className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg ml-1 transition"
                          >
                            {saving ? '...' : 'حفظ'}
                          </button>
                          <button
                            onClick={() => setEditId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 transition"
                          >
                            إلغاء
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-5 py-3">
                          {row.imageUrl ? (
                            <img src={resolveImg(row.imageUrl)} alt="" className="w-12 h-12 object-cover rounded-lg border border-gray-200" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-xl">{row.icon || '🚐'}</div>
                          )}
                        </td>
                        <td className="px-5 py-3 font-medium text-gray-800">{row.icon} {row.name}</td>
                        <td className="px-5 py-3 text-gray-600">{row.minCapacity}-{row.maxCapacity} مقعد</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${row.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {row.isActive !== false ? 'نشط' : 'موقوف'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-left">
                          <button
                            onClick={() => startEdit(row)}
                            className="text-xs text-blue-600 hover:text-blue-800 ml-3 transition"
                          >
                            تعديل
                          </button>
                          {row.isActive !== false ? (
                            <button
                              onClick={() => handleDeactivate(row)}
                              className="text-xs text-red-500 hover:text-red-700 transition"
                            >
                              إلغاء التفعيل
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivate(row)}
                              className="text-xs text-emerald-600 hover:text-emerald-700 transition"
                            >
                              تفعيل
                            </button>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleTypesPage;
